import React, { useEffect, useState } from "react";
import { createGlobalStyle, ThemeProvider } from "styled-components";
import { HashRouter as Router, Route, Routes } from "react-router-dom";
import Home from "./containers/home";
import PrivacyPolicy from "./containers/privacy-policy";

const sharedTheme = {
  mobileBreakpoint: "900px",
};

const lightTheme = {
  ...sharedTheme,
  colorScheme: "light",
  colors: {
    ink: "#17313c",
    muted: "#3f5662",
    accent: "#2da7b7",
    accentDark: "#155865",
    navy: "#24384c",
    gold: "#d8ae6d",
    green: "#8db24b",
    surface: "#fbf4ee",
    surfaceMuted: "#eadfd3",
    border: "rgba(23, 49, 60, 0.12)",
    shadow: "0 30px 70px rgba(22, 52, 64, 0.14)",
    cardShadow: "0 18px 34px rgba(22, 52, 64, 0.08)",
    navSurface: "rgba(255, 252, 247, 0.78)",
    cardSurface: "rgba(255, 252, 247, 0.8)",
    cardBorderStrong: "rgba(255, 255, 255, 0.8)",
    previewFrameStart: "#efe0d1",
    previewFrameEnd: "#ffffff",
    previewFrameBorder: "rgba(31, 41, 55, 0.08)",
    backgroundSpotOne: "rgba(45, 167, 183, 0.24)",
    backgroundSpotTwo: "rgba(141, 178, 75, 0.18)",
    backgroundStart: "#f1e4d8",
    backgroundMid: "#f7eee5",
    backgroundEnd: "#fcf8f3",
    secondaryButtonBackground: "rgba(255, 255, 255, 0.94)",
    secondaryButtonHoverShadow: "0 16px 28px rgba(23, 49, 60, 0.08)",
    bottomPanelStart: "#24384c",
    bottomPanelEnd: "#155865",
    bottomPanelShadow: "0 24px 46px rgba(22, 52, 64, 0.22)",
    primaryButtonStart: "#1f6c78",
    primaryButtonEnd: "#155865",
    primaryButtonHoverStart: "#155865",
    primaryButtonHoverEnd: "#124954",
  },
};

const darkTheme = {
  ...sharedTheme,
  colorScheme: "dark",
  colors: {
    ink: "#e6edf8",
    muted: "#a8b7ca",
    accent: "#38a8da",
    accentDark: "#74d0ff",
    navy: "#0f1a2b",
    gold: "#efc238",
    green: "#8db24b",
    surface: "#121f33",
    surfaceMuted: "#1a2c45",
    border: "rgba(148, 182, 214, 0.2)",
    shadow: "0 30px 70px rgba(5, 13, 24, 0.42)",
    cardShadow: "0 18px 34px rgba(5, 13, 24, 0.24)",
    navSurface: "rgba(17, 32, 52, 0.88)",
    cardSurface: "rgba(17, 32, 52, 0.82)",
    cardBorderStrong: "rgba(109, 146, 184, 0.22)",
    previewFrameStart: "#16253b",
    previewFrameEnd: "#0d1728",
    previewFrameBorder: "rgba(109, 146, 184, 0.18)",
    backgroundSpotOne: "rgba(56, 168, 218, 0.18)",
    backgroundSpotTwo: "rgba(141, 178, 75, 0.14)",
    backgroundStart: "#08111e",
    backgroundMid: "#0c1829",
    backgroundEnd: "#13243a",
    secondaryButtonBackground: "rgba(15, 26, 43, 0.72)",
    secondaryButtonHoverShadow: "0 16px 28px rgba(5, 13, 24, 0.28)",
    bottomPanelStart: "#0f1a2b",
    bottomPanelEnd: "#124954",
    bottomPanelShadow: "0 24px 46px rgba(5, 13, 24, 0.36)",
    primaryButtonStart: "#1f6c78",
    primaryButtonEnd: "#155865",
    primaryButtonHoverStart: "#155865",
    primaryButtonHoverEnd: "#124954",
  },
};

function getSystemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function App() {
  const [manualTheme, setManualTheme] = useState(null);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);
  const resolvedTheme = manualTheme || (systemPrefersDark ? "dark" : "light");
  const theme = resolvedTheme === "dark" ? darkTheme : lightTheme;

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event) => {
      setSystemPrefersDark(event.matches);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    document.body.setAttribute("data-theme", resolvedTheme);
  }, [resolvedTheme]);

  const toggleTheme = () => {
    setManualTheme((currentTheme) => {
      const activeTheme = currentTheme || (systemPrefersDark ? "dark" : "light");
      return activeTheme === "dark" ? "light" : "dark";
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <div>
          <Routes>
            <Route
              path="/"
              element={
                <Home
                  isDarkTheme={resolvedTheme === "dark"}
                  onToggleTheme={toggleTheme}
                />
              }
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
          <GlobalStyle />
        </div>
      </Router>
    </ThemeProvider>
  );
}

const GlobalStyle = createGlobalStyle`
  :root {
    color-scheme: ${({ theme }) => theme.colorScheme};
  }

  html,
  body {
    margin: 0;
    padding: 0;
    min-height: 100%;
  }

  * {
    box-sizing: border-box;
  }

  html {
    background:
      radial-gradient(
        circle at top left,
        ${({ theme }) => theme.colors.backgroundSpotOne},
        transparent 34%
      ),
      radial-gradient(
        circle at top right,
        ${({ theme }) => theme.colors.backgroundSpotTwo},
        transparent 24%
      ),
      linear-gradient(
        180deg,
        ${({ theme }) => theme.colors.backgroundStart} 0%,
        ${({ theme }) => theme.colors.backgroundMid} 44%,
        ${({ theme }) => theme.colors.backgroundEnd} 100%
      );
  }

  body {
    font-family: "Manrope", "Segoe UI", sans-serif;
    color: ${({ theme }) => theme.colors.ink};
    background: transparent;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    min-height: 100vh;
  }

  *:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.accentDark};
    outline-offset: 3px;
  }

  h1,
  h2,
  h3,
  h4,
  button,
  a {
    font-family: "Space Grotesk", "Segoe UI", sans-serif;
  }

  a {
    color: inherit;
    text-decoration: none;
  }
`;

export default App;
